import { ReactNode } from 'react';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { ScarcityColoredDiv } from '@sorare/core/src/atoms/ui/ScarcityColoredDiv';

const LineupRow = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  display: grid;
  align-items: center;
  grid-template-columns: var(--quadruple-unit) 1fr calc(8 * var(--unit));
  grid-column-gap: var(--intermediate-unit);
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  background-color: rgba(var(--c-rgb-white), 0.1);
`;

const Relative = styled.div`
  position: relative;
  isolation: isolate;
`;

const StyledScarcityColoredDiv = styled(ScarcityColoredDiv)`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: var(--half-unit);
`;

const LeaderboardIcon = styled.img`
  width: var(--quadruple-unit);
  height: var(--quadruple-unit);
`;

const Captain = styled(Horizontal).attrs({ center: true })`
  position: absolute;
  top: calc(-1 * var(--half-unit));
  left: calc(-1 * var(--half-unit));
  height: var(--intermediate-unit);
  width: var(--intermediate-unit);
  background-color: var(--c-yellow-400);
  color: var(--c-black);
  border-radius: var(--unit);
  font-size: var(--unit);
  font-weight: bold;
`;

const ComposeTeamObject = styled.img`
  background-color: var(--c-nd-50);
  width: calc(5 * var(--unit));
  height: calc(5 * var(--unit));
  object-fit: contain;
  object-position: center;
  border-radius: var(--unit);
`;

type Props = {
  iconUrl: Nullable<string>;
  rarity: Nullable<Rarity>;
  appearances?: {
    id: string;
    index: number;
    captain?: boolean;
    avatarPictureUrl: string;
    playerDisplayName: string;
  }[];
  status?: ReactNode;
};

export const LineupItem = ({
  iconUrl,
  rarity,
  status,
  appearances = [],
}: Props) => {
  const sortedAppearances = [...appearances].sort((a, b) => a.index - b.index);
  return (
    <LineupRow>
      <StyledScarcityColoredDiv className={rarity!} />
      {!!iconUrl && <LeaderboardIcon src={iconUrl} />}
      <Horizontal>
        {sortedAppearances.map(
          ({ captain, id, playerDisplayName, avatarPictureUrl }) => (
            <Relative key={id}>
              {!!captain && <Captain>C</Captain>}
              <Tooltip title={playerDisplayName}>
                <ComposeTeamObject alt="player" src={avatarPictureUrl} />
              </Tooltip>
            </Relative>
          )
        )}
      </Horizontal>
      {status}
    </LineupRow>
  );
};
